[
  {
    "path": "/help/getting-started",
    "category": "start",
    "title": "Getting started with RAQOZ",
    "summary": "Set up an organisation workspace and understand the accountable purchase journey before live buying begins.",
    "audience": ["Organisation administrator", "Procurement", "Finance"],
    "guidance": [
      "Create and verify your account, then create or join the correct organisation workspace.",
      "Complete the organisation structure, team access, approval controls and procurement policy before inviting broad use.",
      "Use one small purchase to confirm the full path from request to retained evidence."
    ],
    "evidence": ["Organisation and membership records", "Attributable setup changes", "The first connected purchase record"],
    "keywords": ["onboarding", "setup", "workspace", "first steps"]
  },
  {
    "path": "/help/first-purchase",
    "category": "start",
    "title": "Run your first purchase",
    "summary": "Take one bounded purchase through request, approval, sourcing, order, receipt and invoice review.",
    "audience": ["Requester", "Approver", "Procurement", "Receiver", "Finance"],
    "guidance": [
      "Choose a low-risk purchase with a clear specification, accountable requester and available approver.",
      "Keep supplier selection separate from the request and record the reason for the final selection.",
      "Issue the purchase order, record what was received and review the invoice against the connected records.",
      "Review the resulting Control Passport or audit timeline for missing evidence before widening the rollout."
    ],
    "evidence": ["Requisition and approval decision", "Supplier quotations and selection rationale", "Purchase order, receipt and invoice review"],
    "keywords": ["first purchase", "pilot", "end to end", "walkthrough"]
  },
  {
    "path": "/help/glossary",
    "category": "start",
    "title": "RAQOZ glossary",
    "summary": "Use one shared vocabulary for requests, sourcing, orders, receipts, invoices, controls and evidence.",
    "audience": ["Everyone"],
    "guidance": [
      "A requisition records the internal need; an RFQ asks suppliers to quote; a purchase order records the authorised commitment.",
      "A goods receipt records what arrived; invoice matching reviews the invoice against connected order and receipt evidence.",
      "A Control Passport is an evidence view for a purchase, not a compliance score, audit opinion or certification.",
      "A waiver is a bounded maker-checker exception; provenance states where a record came from."
    ],
    "evidence": ["This reference article creates no product record"],
    "keywords": ["glossary", "definitions", "terminology", "control passport"]
  },
  {
    "path": "/help/roles/administrator",
    "category": "roles",
    "title": "Organisation administrator guide",
    "summary": "Configure the tenant, access, controls and operating structure without becoming the default operator for every purchase.",
    "audience": ["Organisation administrator"],
    "guidance": [
      "Maintain organisation settings, locations, departments and membership in the correct tenant.",
      "Assign the narrowest role that matches each person's real responsibility.",
      "Prepare approval controls and procurement policies as drafts, review them independently and activate only approved versions.",
      "Use simulations, audit events and exports to verify configuration changes."
    ],
    "evidence": ["Membership and role changes", "Configuration revisions and approvals", "Activation and simulation events"],
    "keywords": ["administrator", "tenant admin", "configuration", "access"]
  },
  {
    "path": "/help/roles/requester",
    "category": "roles",
    "title": "Requester guide",
    "summary": "Describe the business need clearly and submit a complete request without deciding the supplier or approving yourself.",
    "audience": ["Requester"],
    "guidance": [
      "State what is needed, why it is needed, quantity, timing, location and department.",
      "Attach the specification and supporting context required by your organisation's policy.",
      "Submit the request for independent approval and respond to recorded questions or returns.",
      "Track progress from the requisition record rather than creating a second request."
    ],
    "evidence": ["Requisition lines and business purpose", "Attachments", "Submission and change history"],
    "keywords": ["requester", "requisition", "request", "submit"]
  },
  {
    "path": "/help/roles/approver",
    "category": "roles",
    "title": "Approver guide",
    "summary": "Make attributable decisions within assigned authority and record enough reasoning for later review.",
    "audience": ["Approver"],
    "guidance": [
      "Review only decisions assigned to you and confirm the request is within your authority.",
      "Consider purpose, specification, budget context and policy results before deciding.",
      "Approve, return or reject with a concise reason that another reviewer can understand.",
      "Do not use approval to replace procurement's supplier-selection responsibility."
    ],
    "evidence": ["Approver identity and assignment", "Decision, timestamp and reason", "Policy context at the decision"],
    "keywords": ["approver", "approval", "authority", "decision"]
  },
  {
    "path": "/help/roles/procurement",
    "category": "roles",
    "title": "Procurement guide",
    "summary": "Run sourcing, supplier comparison, selection and purchase-order preparation with traceable commercial reasoning.",
    "audience": ["Procurement"],
    "guidance": [
      "Start sourcing from an approved requisition and preserve its requirements.",
      "Invite legitimate suppliers through the RFQ and keep submitted quotations attributable.",
      "Compare admitted quotations consistently and record any non-lowest selection rationale.",
      "Create and issue the purchase order only after the selection is authorised."
    ],
    "evidence": ["RFQ and supplier invitations", "Quotations and comparison", "Selection rationale and purchase order"],
    "keywords": ["procurement", "sourcing", "quotation", "purchase order"]
  },
  {
    "path": "/help/roles/receiver",
    "category": "roles",
    "title": "Receiver guide",
    "summary": "Record what actually arrived, including partial and rejected quantities, without changing the commercial order.",
    "audience": ["Receiver"],
    "guidance": [
      "Open the relevant issued purchase order before recording delivery.",
      "Record accepted and rejected quantities against the correct lines and delivery event.",
      "Attach or reference supporting delivery evidence required by policy.",
      "Leave unresolved shortages or damage visible for procurement and finance."
    ],
    "evidence": ["Goods receipt record", "Accepted and rejected quantities", "Receipt attachments and timestamps"],
    "keywords": ["receiver", "goods receipt", "delivery", "rejected quantity"]
  },
  {
    "path": "/help/roles/finance",
    "category": "roles",
    "title": "Finance guide",
    "summary": "Review invoice and payment-approval evidence while keeping bank execution outside RAQOZ.",
    "audience": ["Finance"],
    "guidance": [
      "Record or review the invoice against the connected purchase order and receipt evidence.",
      "Resolve or escalate matching exceptions through the governed correction or override path.",
      "Record the payment-approval state and supporting evidence.",
      "Execute payment in the authorised banking process; RAQOZ does not move money."
    ],
    "evidence": ["Invoice and matching result", "Exception resolution or override", "Payment-approval state and evidence"],
    "keywords": ["finance", "invoice", "matching", "payment approval"]
  },
  {
    "path": "/help/roles/auditor",
    "category": "roles",
    "title": "Auditor guide",
    "summary": "Review records and timelines without creating, changing or approving operational transactions.",
    "audience": ["Auditor"],
    "guidance": [
      "Use read-only access to inspect the connected purchase record and its control context.",
      "Check attribution, chronology, policy version, exceptions and provenance.",
      "Export evidence through approved product functions where available.",
      "Treat RAQOZ evidence as source material, not an audit conclusion or assurance opinion."
    ],
    "evidence": ["Review access is recorded where supported", "Exports retain source context", "Operational records remain unchanged"],
    "keywords": ["auditor", "read only", "audit trail", "evidence"]
  },
  {
    "path": "/help/workflows/requisition",
    "category": "workflows",
    "title": "Create and submit a requisition",
    "summary": "Capture a complete business need before approval or supplier commitment.",
    "audience": ["Requester"],
    "guidance": [
      "Create a requisition and add clear line descriptions, quantities, units and expected timing.",
      "Select the correct organisational context and add the business purpose.",
      "Attach specifications or evidence required by the active policy.",
      "Review completeness, then submit for independent approval."
    ],
    "evidence": ["Requisition and lines", "Business purpose and organisational context", "Attachments and submission history"],
    "keywords": ["requisition", "purchase request", "submit", "specification"]
  },
  {
    "path": "/help/workflows/approval",
    "category": "workflows",
    "title": "Review and decide an approval",
    "summary": "Apply assigned authority and record an attributable approval, return or rejection.",
    "audience": ["Approver"],
    "guidance": [
      "Open the assigned decision and confirm the request, value and organisational context.",
      "Review budget and policy results shown with the request.",
      "Record the decision and a concise reason.",
      "Return incomplete requests instead of resolving missing evidence outside the system."
    ],
    "evidence": ["Assignment and authority context", "Decision and reason", "Decision timestamp and actor"],
    "keywords": ["approval", "approve", "return", "reject"]
  },
  {
    "path": "/help/workflows/rfq",
    "category": "workflows",
    "title": "Create and manage an RFQ",
    "summary": "Turn approved requirements into a controlled request for comparable supplier quotations.",
    "audience": ["Procurement"],
    "guidance": [
      "Create the RFQ from the approved requirement and preserve mandatory specifications.",
      "Choose legitimate suppliers and set a clear response window.",
      "Issue invitations through the governed supplier-link workflow.",
      "Close or advance the RFQ only when responses are ready for review."
    ],
    "evidence": ["RFQ scope and dates", "Invited suppliers", "Invitation and response events"],
    "keywords": ["rfq", "request for quotation", "invite suppliers", "sourcing"]
  },
  {
    "path": "/help/workflows/supplier-quotation",
    "category": "workflows",
    "title": "Receive a supplier quotation",
    "summary": "Keep each supplier response attributable, comparable and linked to the correct RFQ.",
    "audience": ["Procurement", "Supplier participant"],
    "guidance": [
      "Use the invitation link associated with the intended supplier and RFQ.",
      "Record prices, currency, scope qualifications and supporting documents accurately.",
      "Preserve the response provenance instead of copying figures into an unrelated record.",
      "Do not treat submission as selection or a purchase commitment."
    ],
    "evidence": ["Supplier and RFQ association", "Quoted lines, currency and conditions", "Submission provenance and attachments"],
    "keywords": ["supplier quotation", "quote", "supplier link", "response"]
  },
  {
    "path": "/help/workflows/quotation-comparison",
    "category": "workflows",
    "title": "Compare supplier quotations",
    "summary": "Compare admitted responses on consistent requirements before recording a supplier decision.",
    "audience": ["Procurement", "Approver"],
    "guidance": [
      "Confirm each admitted quotation answers the same requirement or identify the difference.",
      "Compare price, scope, conditions and relevant supplier-control results.",
      "Keep excluded or incomplete responses visible with the reason for their treatment.",
      "Record the recommended selection before creating the order."
    ],
    "evidence": ["Admitted quotations", "Normalised comparison", "Exclusions and recommendation"],
    "keywords": ["quotation comparison", "bid comparison", "supplier selection", "quote"]
  },
  {
    "path": "/help/workflows/non-l1-selection",
    "category": "workflows",
    "title": "Record a non-lowest-price selection",
    "summary": "Explain why a supplier other than the lowest evaluated price is the accountable choice.",
    "audience": ["Procurement", "Approver"],
    "guidance": [
      "Confirm the comparison is complete and that the price difference is understood.",
      "Record the operational, technical, risk or commercial reason for the selection.",
      "Satisfy any active rule requiring additional approval, evidence or waiver.",
      "Keep the chosen quotation and rationale linked to the purchase order."
    ],
    "evidence": ["Comparison and lowest evaluated option", "Selection rationale", "Required approval or waiver"],
    "keywords": ["non l1", "not lowest", "selection rationale", "award"]
  },
  {
    "path": "/help/workflows/purchase-order",
    "category": "workflows",
    "title": "Create and issue a purchase order",
    "summary": "Convert an authorised supplier selection into a versioned commercial order.",
    "audience": ["Procurement"],
    "guidance": [
      "Create the draft order from the authorised selection rather than rekeying an unrelated order.",
      "Review supplier, lines, quantities, prices, delivery details and terms.",
      "Issue the approved version through the product workflow.",
      "Use a controlled revision when the order must change; do not overwrite prior evidence."
    ],
    "evidence": ["Authorised selection link", "Purchase-order version", "Issue and revision history"],
    "keywords": ["purchase order", "po", "issue order", "version"]
  },
  {
    "path": "/help/workflows/supplier-acknowledgement",
    "category": "workflows",
    "title": "Record supplier acknowledgement",
    "summary": "Preserve whether the supplier acknowledged the issued purchase order and which version they saw.",
    "audience": ["Procurement", "Supplier participant"],
    "guidance": [
      "Open the acknowledgement path linked to the issued purchase order.",
      "Review the supplier, order version and commercial details before responding.",
      "Record acknowledgement or the available exception response.",
      "Resolve material disagreement through a controlled order revision."
    ],
    "evidence": ["Purchase-order version presented", "Supplier response", "Acknowledgement timestamp and provenance"],
    "keywords": ["supplier acknowledgement", "po acknowledgement", "order acceptance"]
  },
  {
    "path": "/help/workflows/goods-receipt",
    "category": "workflows",
    "title": "Record a goods receipt",
    "summary": "Capture accepted, rejected and outstanding quantities against the issued order.",
    "audience": ["Receiver", "Procurement"],
    "guidance": [
      "Select the issued purchase order that corresponds to the delivery.",
      "Record accepted and rejected quantities for each relevant line.",
      "Use separate receipt events for partial deliveries.",
      "Attach the evidence required by the organisation's policy and leave shortages visible."
    ],
    "evidence": ["Receipt event and actor", "Line-level accepted and rejected quantities", "Attachments and outstanding quantities"],
    "keywords": ["goods receipt", "grn", "partial receipt", "delivery"]
  },
  {
    "path": "/help/workflows/invoice-matching",
    "category": "workflows",
    "title": "Review invoice matching",
    "summary": "Compare the invoice with the connected order and recorded receipt evidence.",
    "audience": ["Finance", "Procurement"],
    "guidance": [
      "Record the invoice against the correct purchase order.",
      "Review the product's matching result and the underlying order and receipt evidence.",
      "Investigate differences instead of forcing a clean state.",
      "Use the governed correction or override path when an accountable exception is justified."
    ],
    "evidence": ["Invoice and source order", "Matching result", "Exception, correction or override history"],
    "keywords": ["invoice matching", "three way match", "exception", "invoice"]
  },
  {
    "path": "/help/workflows/payment-approval",
    "category": "workflows",
    "title": "Record payment approval",
    "summary": "Record whether payment is approved and why, while executing the payment outside RAQOZ.",
    "audience": ["Finance", "Approver"],
    "guidance": [
      "Confirm the invoice, match result, exceptions and required approvals are complete.",
      "Record the payment-approval decision and supporting reason or evidence.",
      "Keep unresolved exceptions visible rather than treating approval as a correction.",
      "Complete bank execution in the organisation's authorised payment system."
    ],
    "evidence": ["Payment-approval state", "Approver, timestamp and reason", "Connected invoice and exception evidence"],
    "keywords": ["payment approval", "payment ready", "finance", "bank execution"]
  },
  {
    "path": "/help/workflows/audit-pack",
    "category": "workflows",
    "title": "Prepare a purchase audit pack",
    "summary": "Assemble the connected request-to-payment evidence without presenting it as an audit opinion.",
    "audience": ["Auditor", "Finance", "Procurement"],
    "guidance": [
      "Start from the purchase or Control Passport and confirm the full record chain.",
      "Include the applicable policy version, decisions, supplier evidence, order, receipt, invoice and exceptions.",
      "Use the approved export function and retain provenance for imported evidence.",
      "Have the relevant reviewer assess sufficiency; RAQOZ does not issue assurance."
    ],
    "evidence": ["Connected transaction records", "Policy and control evaluation context", "Export event and source provenance"],
    "keywords": ["audit pack", "evidence export", "audit trail", "control passport"]
  },
  {
    "path": "/help/admin/organisation-settings",
    "category": "administration",
    "title": "Manage organisation settings",
    "summary": "Maintain the legal and operating identity of the correct organisation tenant.",
    "audience": ["Organisation administrator"],
    "guidance": [
      "Confirm you are working in the intended organisation before changing shared settings.",
      "Maintain the legal name, operating name, base currency and other available organisation details.",
      "Review downstream document and reporting effects before saving material changes.",
      "Keep changes attributable through the configured administration workflow."
    ],
    "evidence": ["Organisation settings", "Actor and change time", "Relevant audit events"],
    "keywords": ["organisation settings", "tenant", "legal name", "currency"]
  },
  {
    "path": "/help/admin/locations-and-departments",
    "category": "administration",
    "title": "Configure locations and departments",
    "summary": "Model where purchases are initiated, approved, received and reported.",
    "audience": ["Organisation administrator", "Finance"],
    "guidance": [
      "Create the locations and departments used by real operations.",
      "Use stable names and codes that colleagues can recognise.",
      "Connect budgets and approval responsibilities to the correct organisational units.",
      "Retire or rename structures carefully so historical records remain understandable."
    ],
    "evidence": ["Location and department records", "Budget and approval associations", "Configuration change history"],
    "keywords": ["locations", "departments", "branches", "structure"]
  },
  {
    "path": "/help/admin/team-and-access",
    "category": "administration",
    "title": "Manage team and access",
    "summary": "Invite members and assign the narrowest role that matches each person's responsibility.",
    "audience": ["Organisation administrator"],
    "guidance": [
      "Invite the intended work email into the correct organisation.",
      "Assign one of the supported roles based on the person's actual duties.",
      "Check that approvers and operational users can reach the records they need.",
      "Remove or change access promptly when responsibilities change."
    ],
    "evidence": ["Invitation and membership status", "Assigned role", "Access changes and actor"],
    "keywords": ["team", "invite", "membership", "access", "roles"]
  },
  {
    "path": "/help/admin/budgets",
    "category": "administration",
    "title": "Configure budgets",
    "summary": "Provide budget context for purchase decisions at the relevant organisational level.",
    "audience": ["Organisation administrator", "Finance", "Approver"],
    "guidance": [
      "Define budgets for the organisational units and periods represented in the product.",
      "Confirm budget ownership and the available control posture.",
      "Review how current commitments affect available context before relying on a figure.",
      "Change budgets through the attributable configuration path rather than hiding an exception."
    ],
    "evidence": ["Budget configuration", "Organisational association", "Changes and decision context"],
    "keywords": ["budgets", "spend control", "department", "commitment"]
  },
  {
    "path": "/help/admin/approval-controls",
    "category": "administration",
    "title": "Configure approval controls",
    "summary": "Route requests to accountable approvers using the supported authority configuration.",
    "audience": ["Organisation administrator", "Approver", "Finance"],
    "guidance": [
      "Document the organisation's authority model before configuring it.",
      "Configure the available value and organisational conditions and assign eligible approvers.",
      "Keep requester and approver responsibilities separated.",
      "Test representative cases before treating the route as ready for live use."
    ],
    "evidence": ["Approval-control configuration", "Assignments and policy context", "Test or simulation results"],
    "keywords": ["approval controls", "authority", "routing", "approver"]
  },
  {
    "path": "/help/admin/procurement-policies",
    "category": "administration",
    "title": "Manage procurement policies",
    "summary": "Define effective-dated purchase rules and evidence expectations for consistent control evaluation.",
    "audience": ["Organisation administrator", "Procurement", "Auditor"],
    "guidance": [
      "Create a draft policy from the organisation's approved procurement requirements.",
      "Define the available scope, rule conditions, evidence requirements and exception treatment.",
      "Review the draft independently and activate only the approved version.",
      "Do not describe policy configuration as legal, statutory or certification compliance."
    ],
    "evidence": ["Policy draft and scope", "Review and activation events", "Effective-dated active version"],
    "keywords": ["procurement policy", "purchase policy", "evidence requirements", "effective date"]
  },
  {
    "path": "/help/admin/policy-versioning",
    "category": "administration",
    "title": "Version procurement policies",
    "summary": "Preserve which policy version applied to each evaluation instead of rewriting history.",
    "audience": ["Organisation administrator", "Auditor"],
    "guidance": [
      "Create a new draft when a policy must change.",
      "Record the reason, scope and intended effective date for the revision.",
      "Complete review and activation through the governed lifecycle.",
      "Keep prior versions available for the purchases evaluated under them."
    ],
    "evidence": ["Version history and reason", "Approval and activation", "Evaluation-to-version association"],
    "keywords": ["policy version", "effective date", "history", "activation"]
  },
  {
    "path": "/help/admin/overrides-and-waivers",
    "category": "administration",
    "title": "Manage overrides and waivers",
    "summary": "Use bounded maker-checker exceptions without erasing the control result that prompted them.",
    "audience": ["Organisation administrator", "Approver", "Procurement", "Finance"],
    "guidance": [
      "Confirm the underlying control result and evidence gap before requesting an exception.",
      "State the reason, scope and validity required for the waiver or override.",
      "Send the request to an independent eligible decision maker.",
      "Keep the original result, decision and later consumption connected."
    ],
    "evidence": ["Original control result", "Exception request and rationale", "Independent decision and bounded use"],
    "keywords": ["waiver", "override", "exception", "maker checker"]
  },
  {
    "path": "/help/admin/policy-simulation",
    "category": "administration",
    "title": "Simulate a procurement policy",
    "summary": "Test proposed control behavior without writing live transaction states.",
    "audience": ["Organisation administrator", "Procurement", "Auditor"],
    "guidance": [
      "Select the draft policy and representative purchase scenarios.",
      "Run the no-write simulation and review which rules would pass, observe or block.",
      "Investigate unexpected results and adjust the draft, not historical transactions.",
      "Retain the simulation result as review evidence before activation."
    ],
    "evidence": ["Draft policy reference", "Simulation inputs and outputs", "Reviewer decision before activation"],
    "keywords": ["policy simulation", "no write", "test controls", "observe enforce"]
  },
  {
    "path": "/help/controls/control-passport",
    "category": "controls",
    "title": "Understand a Control Passport",
    "summary": "Review the permanent control identity, policy context, evidence, exceptions and provenance for one purchase.",
    "audience": ["Procurement", "Finance", "Auditor"],
    "guidance": [
      "Open the passport associated with the purchase and confirm its permanent Control ID.",
      "Review the policy version and effective context used for the evaluation.",
      "Inspect present and missing evidence, supplier-control results and open exceptions.",
      "Use the passport as an evidence view, not as a compliance certificate or audit opinion."
    ],
    "evidence": ["Permanent Control ID", "Immutable evaluation and policy context", "Evidence map, exceptions and provenance"],
    "keywords": ["control passport", "control id", "evaluation", "evidence"]
  },
  {
    "path": "/help/controls/supplier-qualification",
    "category": "controls",
    "title": "Record supplier qualification",
    "summary": "Document the organisation's supplier qualification decision, scope and rationale.",
    "audience": ["Procurement", "Organisation administrator", "Auditor"],
    "guidance": [
      "Review the supplier information and evidence required by the active policy.",
      "Record the organisation-specific qualification status and rationale.",
      "Set or update available risk and criticality context carefully.",
      "Reassess when evidence, scope or the organisation's policy changes."
    ],
    "evidence": ["Supplier evidence", "Qualification status and rationale", "Actor, time and policy context"],
    "keywords": ["supplier qualification", "vendor qualification", "risk", "criticality"]
  },
  {
    "path": "/help/controls/approved-supplier-list",
    "category": "controls",
    "title": "Use the approved supplier list",
    "summary": "Apply the organisation's approved-supplier decision without presenting it as a universal endorsement.",
    "audience": ["Procurement", "Requester", "Auditor"],
    "guidance": [
      "Check the supplier's status for the relevant organisation and purchasing context.",
      "Confirm required qualification evidence remains current for the intended purchase.",
      "Use the configured exception path when a non-approved supplier is genuinely required.",
      "Treat list status as an internal control decision, not a public guarantee."
    ],
    "evidence": ["Organisation-specific supplier status", "Supporting qualification record", "Exception where applicable"],
    "keywords": ["approved supplier list", "asl", "approved vendor", "supplier status"]
  },
  {
    "path": "/help/controls/evidence-mapping",
    "category": "controls",
    "title": "Map control evidence",
    "summary": "Connect required evidence to the source records that demonstrate how a purchase was handled.",
    "audience": ["Organisation administrator", "Procurement", "Auditor"],
    "guidance": [
      "Start with an approved policy requirement or control objective.",
      "Map it to the source record that provides the evidence rather than a copied summary.",
      "Mark imported historical records with their provenance and evidence-only boundary.",
      "Review missing or ambiguous mappings before relying on an evaluation."
    ],
    "evidence": ["Control requirement", "Mapped source record", "Provenance and missing-evidence state"],
    "keywords": ["evidence mapping", "control evidence", "provenance", "source record"]
  },
  {
    "path": "/help/controls/exceptions",
    "category": "controls",
    "title": "Review control exceptions",
    "summary": "Investigate control failures or missing evidence while preserving the original evaluation.",
    "audience": ["Procurement", "Approver", "Finance", "Auditor"],
    "guidance": [
      "Open the exception and review the rule, checkpoint and source evidence.",
      "Correct the underlying record when it is genuinely wrong or incomplete.",
      "Use a bounded waiver or override only when an accountable exception is justified.",
      "Do not delete or rewrite the original evaluation."
    ],
    "evidence": ["Original evaluation", "Correction, waiver or override", "Decision rationale and actor"],
    "keywords": ["control exception", "failure", "missing evidence", "waiver"]
  },
  {
    "path": "/help/controls/audit-timeline",
    "category": "controls",
    "title": "Read the audit timeline",
    "summary": "Reconstruct who did what, when, and against which purchase and policy records.",
    "audience": ["Auditor", "Organisation administrator", "Procurement", "Finance"],
    "guidance": [
      "Start from the purchase or control record whose history you need to understand.",
      "Read events in sequence and confirm actor, time, action and source record.",
      "Compare material decisions with the policy version and evidence available at that point.",
      "Use corrections as later attributable events rather than expecting history to disappear."
    ],
    "evidence": ["Attributable event sequence", "Source-record references", "Policy and correction context"],
    "keywords": ["audit timeline", "audit log", "history", "events"]
  },
  {
    "path": "/help/imports/suppliers",
    "category": "imports",
    "title": "Import suppliers",
    "summary": "Map, validate and import supplier records while handling duplicates deliberately.",
    "audience": ["Procurement", "Organisation administrator"],
    "guidance": [
      "Prepare the supplier file using consistent identifiers and contact data.",
      "Map source columns to the available RAQOZ fields.",
      "Review validation and duplicate results before writing records.",
      "Choose skip or update behavior deliberately and retain the import outcome."
    ],
    "evidence": ["Source and column mapping", "Validation preview", "Created, updated, skipped and failed outcomes"],
    "keywords": ["import suppliers", "supplier spreadsheet", "duplicates", "mapping"]
  },
  {
    "path": "/help/imports/inventory",
    "category": "imports",
    "title": "Import inventory",
    "summary": "Bring item or inventory reference data into RAQOZ with validation before write.",
    "audience": ["Procurement", "Organisation administrator"],
    "guidance": [
      "Prepare stable item identifiers, descriptions, units and other supported fields.",
      "Map each source column and inspect the validation preview.",
      "Resolve required-field and duplicate errors before import.",
      "Review the recorded result after the write completes."
    ],
    "evidence": ["Source mapping", "Validation and duplicate decisions", "Import result by row"],
    "keywords": ["import inventory", "items", "catalogue", "spreadsheet"]
  },
  {
    "path": "/help/imports/control-matrix",
    "category": "imports",
    "title": "Import a control matrix",
    "summary": "Load control-matrix reference data for governed review rather than silently activating policy.",
    "audience": ["Organisation administrator", "Auditor"],
    "guidance": [
      "Prepare control identifiers, descriptions and supported mapping fields consistently.",
      "Map columns and review validation before any write.",
      "Import the matrix as governed reference evidence.",
      "Review, approve and activate live policy separately through the policy lifecycle."
    ],
    "evidence": ["Source matrix and mapping", "Validation outcome", "Imported reference records and provenance"],
    "keywords": ["control matrix", "import controls", "policy", "mapping"]
  },
  {
    "path": "/help/imports/historical-data",
    "category": "imports",
    "title": "Import historical purchase data",
    "summary": "Preserve prior records as provenance-labelled evidence without creating live operational states.",
    "audience": ["Organisation administrator", "Finance", "Auditor"],
    "guidance": [
      "Prepare historical identifiers, dates and source references needed for reconstruction.",
      "Map and validate the file, including duplicate treatment.",
      "Import history as evidence-only records with explicit provenance.",
      "Never treat imported history as a live approval, receipt, payment state or current control decision."
    ],
    "evidence": ["Source and mapping", "Evidence-only provenance", "Import outcomes and validation errors"],
    "keywords": ["historical import", "migration", "evidence only", "provenance"]
  },
  {
    "path": "/help/troubleshooting/invitations",
    "category": "troubleshooting",
    "title": "Troubleshoot team invitations",
    "summary": "Resolve a missing, expired or misdirected organisation invitation safely.",
    "audience": ["Organisation administrator", "Invited member"],
    "guidance": [
      "Confirm the invitation was sent to the intended work email and organisation.",
      "Check the recorded invitation state before sending duplicates.",
      "Use the available resend or replacement path when the original invitation cannot be used.",
      "Do not share invitation links between people."
    ],
    "evidence": ["Invitation email and organisation", "Invitation status", "Resend or replacement event"],
    "keywords": ["invitation", "invite", "expired link", "team"]
  },
  {
    "path": "/help/troubleshooting/permissions",
    "category": "troubleshooting",
    "title": "Troubleshoot permissions",
    "summary": "Check tenant membership, assigned role and record scope when a screen or action is unavailable.",
    "audience": ["Everyone"],
    "guidance": [
      "Confirm the member is signed into the correct organisation.",
      "Compare the assigned role with the responsibility required for the action.",
      "Check record-specific assignment, such as an approval assigned to another person.",
      "Ask an organisation administrator to correct access; do not share credentials."
    ],
    "evidence": ["Membership and tenant", "Assigned role", "Record assignment and access change"],
    "keywords": ["permissions", "access denied", "missing screen", "role"]
  },
  {
    "path": "/help/troubleshooting/approval-not-visible",
    "category": "troubleshooting",
    "title": "Approval is not visible",
    "summary": "Find why an expected approval is absent from the assigned decision queue.",
    "audience": ["Approver", "Organisation administrator", "Requester"],
    "guidance": [
      "Confirm the requisition was submitted and is waiting for approval.",
      "Check the approver's role, organisation and assignment for the request.",
      "Review the active approval-control context for the value and organisational unit.",
      "Correct configuration or assignment through the governed administration path."
    ],
    "evidence": ["Requisition status", "Approval assignment", "Applicable control and configuration change"],
    "keywords": ["approval missing", "not visible", "approval queue", "assignment"]
  },
  {
    "path": "/help/troubleshooting/supplier-links",
    "category": "troubleshooting",
    "title": "Supplier link does not work",
    "summary": "Resolve an invalid, expired or incorrect supplier invitation without exposing another supplier's response.",
    "audience": ["Procurement", "Supplier participant"],
    "guidance": [
      "Confirm the link belongs to the intended RFQ, supplier and response stage.",
      "Check whether it has expired, already been used or been replaced.",
      "Issue a replacement through the supported procurement workflow when required.",
      "Never forward a different supplier's link."
    ],
    "evidence": ["Invitation and supplier association", "Link status", "Replacement event where applicable"],
    "keywords": ["supplier link", "rfq invitation", "expired", "quotation"]
  },
  {
    "path": "/help/troubleshooting/import-errors",
    "category": "troubleshooting",
    "title": "Resolve import errors",
    "summary": "Use validation and row outcomes to correct a source file without creating partial uncertainty.",
    "audience": ["Organisation administrator", "Procurement", "Finance"],
    "guidance": [
      "Review required-field, format and mapping errors in the validation preview.",
      "Confirm duplicate handling matches the intended create, skip or update behavior.",
      "Correct the source file or mapping and validate again.",
      "Review the recorded row outcomes after import."
    ],
    "evidence": ["Validation errors", "Mapping and duplicate choice", "Row-level import outcome"],
    "keywords": ["import error", "validation", "duplicate", "mapping"]
  },
  {
    "path": "/help/troubleshooting/matching-exceptions",
    "category": "troubleshooting",
    "title": "Resolve matching exceptions",
    "summary": "Investigate differences among invoice, order and receipt evidence before payment approval.",
    "audience": ["Finance", "Procurement", "Receiver"],
    "guidance": [
      "Open the exception and compare the invoice with the connected purchase order and receipts.",
      "Confirm whether the source record, delivery or invoice is incomplete or incorrect.",
      "Record the missing receipt, controlled correction or accountable override as appropriate.",
      "Keep unresolved differences visible and block informal workarounds."
    ],
    "evidence": ["Matching result", "Underlying order, receipt and invoice", "Resolution or override history"],
    "keywords": ["matching exception", "invoice mismatch", "receipt", "correction"]
  },
  {
    "path": "/help/troubleshooting/corrections",
    "category": "troubleshooting",
    "title": "Make a controlled correction",
    "summary": "Correct an error with an attributable event instead of erasing the earlier record.",
    "audience": ["Organisation administrator", "Procurement", "Finance"],
    "guidance": [
      "Confirm the source record is wrong and identify the correct value or state.",
      "Use the product's available correction path and give a clear reason.",
      "Obtain independent approval where the configured control requires it.",
      "Verify both the original event and correction remain visible in history."
    ],
    "evidence": ["Original record", "Correction, actor and reason", "Approval and resulting state"],
    "keywords": ["correction", "edit history", "audit trail", "override"]
  },
  {
    "path": "/help/troubleshooting/evidence-export",
    "category": "troubleshooting",
    "title": "Troubleshoot an evidence export",
    "summary": "Check access, filters, record completeness and provenance when an export is missing or incomplete.",
    "audience": ["Auditor", "Finance", "Organisation administrator"],
    "guidance": [
      "Confirm you have access to the correct organisation and export function.",
      "Check the purchase, date and status filters used for the export.",
      "Review whether expected evidence exists and is connected to the source purchase.",
      "Keep imported historical evidence labelled and do not treat the export as assurance."
    ],
    "evidence": ["Export request and filters", "Included source records", "Provenance and missing-evidence context"],
    "keywords": ["evidence export", "audit pack", "missing export", "download"]
  },
  {
    "path": "/help/security/access-and-tenant-isolation",
    "category": "security",
    "title": "Access and tenant isolation",
    "summary": "Understand how organisation membership, roles and server-side controls scope access.",
    "audience": ["Organisation administrator", "Auditor", "Everyone"],
    "guidance": [
      "Use a separate authenticated identity for every person; never share passwords or one-time codes.",
      "Confirm membership and role changes are made in the intended organisation.",
      "Treat hidden navigation as usability only; server-side and data-layer checks are the security boundary.",
      "Report suspected cross-tenant or unauthorised access immediately and avoid redistributing exposed data."
    ],
    "evidence": ["Organisation membership", "Role and access events", "Tenant-scoped source records"],
    "keywords": ["security", "tenant isolation", "access control", "roles"]
  },
  {
    "path": "/help/security/audit-logs-and-documents",
    "category": "security",
    "title": "Audit logs and procurement documents",
    "summary": "Handle attributable events and private procurement documents as sensitive organisation records.",
    "audience": ["Organisation administrator", "Auditor", "Finance", "Procurement"],
    "guidance": [
      "Use audit events to understand material actions, actors, times and source records.",
      "Store and share procurement documents only through authorised organisation access.",
      "Use approved exports for legitimate review and protect exported files after download.",
      "Do not email credentials, banking secrets or confidential documents to general support channels."
    ],
    "evidence": ["Attributable audit events", "Document-to-record association", "Export and access context where supported"],
    "keywords": ["audit logs", "documents", "attachments", "security", "exports"]
  }
]
